// Versiya göstəricisi və yeniləmə xəbərdarlığı.
//
// İKİ AYRI ELEMENT, İKİ AYRI MƏQSƏD:
//
//   1. AŞAĞIDA, ORTADA — sönük versiya yazısı. Həmişə oradadır, heç nəyə mane
//      olmur. Dəstək zəngində ilk soruşulan "hansı versiyadır?" sualının
//      cavabı; menyularda axtarmaq lazım gəlmir.
//
//   2. SAĞ KƏNARDA — yalnız yeniləmə olanda görünən dil. Bu, xəbərdarlıqdır:
//      işçi ekranın ortasında pəncərə görmür, amma yeni versiyanın gəldiyini
//      də qaçırmır. Klikləyəndə yan panel açılır.
//
// Heç biri iş axınını kəsmir — POS-da modal pəncərə açıq hesabı örtər və
// işçini nə etdiyindən çıxarardı.

import React, { useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Download, RefreshCw, Sparkles } from 'lucide-react';

import { useI18n } from '../i18n/I18nContext';
import { useAppUpdate } from '../hook/useAppUpdate';
import UpdateDrawer from './UpdateDrawer';

/** `310` → `5:10` */
function clock(totalSec?: number): string {
  if (!totalSec || totalSec <= 0) return '';
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

const AppVersionBadge: React.FC = () => {
  const { update, install, check } = useAppUpdate();
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);

  // Electron xaricində versiya anlayışı yoxdur (brauzerdə həmişə ən sonu
  // yüklənir) — heç nə göstərmirik.
  if (!update) return null;

  const { currentVersion, phase, newVersion, percent, mandatory, restartInSec } = update;
  const hasUpdate = phase === 'downloading' || phase === 'ready';

  return (
    <>
      {/* ─── Sönük versiya yazısı (aşağıda, ortada) ─────────────────────── */}
      <div className="pointer-events-none fixed bottom-0 left-0 right-0 z-[150] flex justify-center pb-1.5">
        <button
          onClick={() => setOpen(true)}
          className="pointer-events-auto select-none px-3 py-1 text-[10px] font-medium tracking-wide text-gray-400/45 transition hover:text-gray-500 dark:text-gray-500/40 dark:hover:text-gray-400"
        >
          v{currentVersion}
        </button>
      </div>

      {/* ─── Kənar dili (sağda) — HƏMİŞƏ görünür ─────────────────────────
          Sakit vaxtda boz və versiyanı yazır; yeniləmə gələndə narıncıya
          dönüb xəbərdarlıq edir. Beləliklə "hansı versiyadayam?" sualının
          cavabı həmişə ekranda olur və eyni yer yeniliklər siyahısını açır. */}
      <AnimatePresence>
        {!open && (
          <motion.button
            initial={{ x: 60, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 60, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 320, damping: 30 }}
            onClick={() => setOpen(true)}
            // DİQQƏT — `bg-[var(--primary)]/95` YAZMAQ OLMAZ.
            //
            // Tailwind arbitrary CSS dəyişəni üçün opasite variantını
            // ÜMUMİYYƏTLƏ yaratmır: `.bg-[var(--primary)]` qaydası çıxır,
            // `/95`-li isə çıxmır. Nəticədə fon şəffaf qalır və düymə ağ
            // ekranda görünmür. Sükutla baş verir — nə build, nə tип
            // yoxlaması xəbərdarlıq etmir; yalnız ekranı görəndə bilinir.
            //
            // `--primary` elə `#f97316`-dır, yəni Tailwind-in `orange-500`-ü.
            className={`fixed right-0 top-1/2 z-[160] flex -translate-y-1/2 items-center gap-1.5 rounded-l-xl shadow-lg transition ${
              hasUpdate
                ? `py-3 pl-3 pr-2.5 text-[11px] font-bold text-white hover:pr-4 ${
                    mandatory && phase === 'ready'
                      ? 'bg-red-600 hover:bg-red-700'
                      : 'bg-orange-500 hover:bg-orange-600'
                  }`
                : // Sakit hal: bozdur və yer tutmur — POS ekranını yükləmir,
                  // amma versiya həmişə görünür və klikləmək mümkündür.
                  'bg-gray-900/25 py-2.5 pl-1.5 pr-1 text-[10px] font-semibold text-white/90 shadow-none hover:bg-gray-900/60 dark:bg-white/10 dark:hover:bg-white/25'
            }`}
          >
            {phase === 'downloading' ? (
              <>
                <Download className="h-4 w-4 shrink-0 animate-pulse" />
                <span className="font-mono">{percent ?? 0}%</span>
              </>
            ) : (
              <>
                {hasUpdate &&
                  (mandatory ? (
                    <RefreshCw className="h-4 w-4 shrink-0" />
                  ) : (
                    <Sparkles className="h-4 w-4 shrink-0" />
                  ))}
                {/* Şaquli yazı: kənarda yer tutmur, amma oxunur. */}
                <span
                  className="whitespace-nowrap uppercase tracking-wider"
                  style={{ writingMode: 'vertical-rl' }}
                >
                  {hasUpdate
                    ? mandatory && restartInSec
                      ? t('update.edgeMandatory', { time: clock(restartInSec) })
                      : t('update.edgeAvailable')
                    : `v${currentVersion}`}
                </span>
              </>
            )}
          </motion.button>
        )}
      </AnimatePresence>

      <UpdateDrawer
        open={open}
        onClose={() => setOpen(false)}
        onInstall={install}
        onCheck={check}
        currentVersion={currentVersion}
        phase={phase}
        newVersion={newVersion}
        lastCheckedAt={update.lastCheckedAt}
        notes={update.notes ?? []}
        lang={lang}
        t={t}
      />
    </>
  );
};

export default AppVersionBadge;
